// Notification feed — the web twin of the mobile notifications tab, shared by
// the Notifications page and the topbar's notifications drawer: counts title,
// All/Unread filter (shown only when something is unread), paged feed (10 per
// page, infinite scroll), auto-mark-read of the unread items on screen.

import type { ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMyNotifications,
  markNotificationRead,
  type Notification,
  type NotificationCounts,
} from '../lib/api';
import { timeAgo } from '../lib/meterCharts';
import Icon, { type IconName } from './Icon';
import { Segmented, Spinner, StateCard } from './ui';

const PAGE_SIZE = 10;

// Notification messages are PHP vsprintf-style templates ('%s') filled from
// parameters[], sometimes carrying HTML — same treatment as mobile.
function formatMessage(n: Notification): string {
  let i = 0;
  const params = n.parameters ?? [];
  const filled = (n.message ?? '').replace(/%[sd]/g, () => {
    const p = params[i++];
    return p === null || p === undefined ? '' : String(p);
  });
  const div = document.createElement('div');
  div.innerHTML = filled;
  return div.textContent ?? filled;
}

function mapIcon(mdi: string): IconName {
  const s = (mdi ?? '').replace(/^mdi-?/, '');
  if (s.includes('alert') || s.includes('warning')) return 'alert-triangle';
  if (s.includes('water')) return 'water';
  if (s.includes('flash') || s.includes('bolt')) return 'flash';
  if (s.includes('check')) return 'check-circle';
  if (s.includes('image') || s.includes('camera')) return 'image';
  return 'bell';
}

function isAlertFlavored(n: Notification): boolean {
  return n.color === 'danger' || mapIcon(n.icon) === 'alert-triangle';
}

type Filter = 'all' | 'unread';

export type NotificationFeedState = {
  title: string;
  subtitle: string;
  unreadCount: number;
  filter: Filter;
  setFilter: (f: Filter) => void;
  items: Notification[];
  loading: boolean;
  error: string | null;
  exhausted: boolean;
  sentinelRef: RefObject<HTMLDivElement | null>;
};

// Counts come from the shell's badge context (AppShell's useNotifCounts),
// passed in so this module doesn't import the shell that renders it.
export function useNotificationFeed(
  counts: NotificationCounts | null,
  refreshCounts: () => void,
): NotificationFeedState {
  const { t } = useTranslation();

  const [filter, setFilter] = useState<Filter>('all');
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);
  const inFlight = useRef(false);

  const loadPage = useCallback((offset: number, replace: boolean, mode: Filter) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    getMyNotifications(PAGE_SIZE, offset, { unread: mode === 'unread' })
      .then((page) => {
        const list = page ?? [];
        setItems((prev) => (replace ? list : [...prev, ...list]));
        setExhausted(list.length < PAGE_SIZE);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => {
        inFlight.current = false;
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setItems([]);
    setExhausted(false);
    loadPage(0, true, filter);
  }, [filter, loadPage]);

  // Infinite scroll: when the sentinel below the list becomes visible, pull
  // the next page.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        loadPage(items.length, false, filter);
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [items.length, filter, exhausted, loading, loadPage]);

  // Auto-mark what's on screen as read, then refresh the badge — mirrors the
  // mobile behavior (there's no per-card "mark read" action).
  useEffect(() => {
    const unread = items.filter((n) => !n.read);
    if (unread.length === 0) return;
    const timer = setTimeout(() => {
      void Promise.allSettled(unread.map((n) => markNotificationRead(n.id))).then(() => {
        setItems((prev) => prev.map((n) => (n.read ? n : { ...n, read: 1 })));
        refreshCounts();
      });
    }, 1200);
    return () => clearTimeout(timer);
  }, [items, refreshCounts]);

  const total = counts?.total ?? 0;
  const unreadCount = counts?.unread ?? 0;

  let subtitle: string;
  if (total === 0) {
    subtitle = t('notifications.emptySubtitle');
  } else if (unreadCount === 0) {
    subtitle = t('notifications.countAllRead', { count: total });
  } else {
    subtitle = `${t('notifications.count', { count: total })} · ${t('notifications.unread', { count: unreadCount })}`;
  }

  return {
    title: unreadCount === 0 && total > 0 ? t('notifications.allCaughtUp') : t('notifications.title'),
    subtitle,
    unreadCount,
    filter,
    setFilter,
    items,
    loading,
    error,
    exhausted,
    sentinelRef,
  };
}

export function NotificationFilter({ feed }: { feed: NotificationFeedState }): ReactElement | null {
  const { t } = useTranslation();
  if (feed.unreadCount === 0) return null;
  return (
    <Segmented
      options={[
        { value: 'all' as const, label: t('notifications.filterAll') },
        {
          value: 'unread' as const,
          label: t('notifications.filterUnreadCount', { count: feed.unreadCount }),
        },
      ]}
      value={feed.filter}
      onChange={feed.setFilter}
    />
  );
}

export function NotificationList({ feed }: { feed: NotificationFeedState }): ReactElement {
  const { t } = useTranslation();
  const { items, loading, error, exhausted, filter } = feed;

  return (
    <>
      {error && <StateCard title={t('notifications.errorTitle')} body={error} />}

      {!error && items.length === 0 && !loading && (
        <StateCard
          icon="bell"
          title={filter === 'unread' ? t('notifications.emptyUnreadTitle') : t('notifications.allCaughtUp')}
          body={filter === 'unread' ? t('notifications.emptyUnreadBody') : t('notifications.emptyAllBody')}
        />
      )}

      <div className="notif-list">
        {items.map((n) => {
          const alert = isAlertFlavored(n);
          return (
            <div key={n.id} className={`notif-card${alert ? ' alert' : ''}`}>
              <span className="notif-icon">
                <Icon name={mapIcon(n.icon)} size={18} />
              </span>
              <div className="notif-body">
                <div className="notif-subject">{n.subject}</div>
                <div className="notif-message">{formatMessage(n)}</div>
                <div className="notif-meta">
                  {timeAgo(n.date)}
                  {n.project?.name ? ` · ${n.project.name}` : ''}
                </div>
              </div>
              {!n.read && <span className="unread-dot" />}
            </div>
          );
        })}
      </div>

      {loading && <Spinner />}

      {/* Infinite scroll sentinel — same behavior as the mobile feed. */}
      {!loading && !error && !exhausted && items.length > 0 && (
        <div ref={feed.sentinelRef} style={{ height: 1 }} />
      )}

      {!loading && !error && exhausted && items.length > 0 && (
        <div className="center">
          <span className="muted">{t('notifications.noMore')}</span>
        </div>
      )}
    </>
  );
}
